import CircleIcon from '@mui/icons-material/Circle';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import PaiHelpIcon from './pai-help-icon';

interface RadioValues {
  label: string;
  infoIconText?: string;
  value: string | number | boolean;
  disabled?: boolean;
}

interface RadioProps {
  label: string;
  infoIconText?: string;
  value: string | number | boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

const PaiRadioItem: React.FC<RadioProps> = ({ label, infoIconText, value, disabled }) => {
  return (
    <FormControlLabel
      value={value}
      control={<Radio checkedIcon={<CircleIcon />} />}
      label={
        infoIconText ? (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <span>{label}</span>
            <PaiHelpIcon tooltipText={infoIconText} variant={'information'} />
          </Stack>
        ) : (
          label
        )
      }
      disabled={disabled}
    />
  );
};

interface Props {
  value: string | number | boolean;
  items: RadioValues[];
  label?: string;
  row?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;

  onChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

const PaiRadioGroup: React.FC<Props> = ({ value, items, label, row, size = 'medium', disabled, onChange }) => {
  const { classes } = useStyles({ size });

  return (
    <FormControl>
      {!_.isNil(label) ? <FormLabel className={classes.label}>{label}</FormLabel> : null}
      <RadioGroup className={classes.radioGroup} value={value} onChange={onChange} row={row}>
        {items.map((item, index) => (
          <PaiRadioItem
            key={index}
            label={item.label}
            infoIconText={item.infoIconText}
            value={item.value}
            disabled={disabled || item.disabled}
            size={size}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

const useStyles = makeStyles<{ size?: 'small' | 'medium' }>()((theme, { size }) => ({
  radioGroup: {
    '& .MuiSvgIcon-root': {
      fontSize: size === 'small' ? theme.typography.pxToRem(16) : size === 'medium' ? theme.typography.pxToRem(20) : theme.typography.pxToRem(20),
    },
    '& .MuiTypography-root': {
      fontSize: size === 'small' ? theme.typography.pxToRem(13) : size === 'medium' ? theme.typography.pxToRem(15) : theme.typography.pxToRem(15),
    },
    '& .MuiButtonBase-root': {
      padding: size === 'small' ? '3px' : size === 'medium' ? '6px' : '6px',
      marginRight: '4px',
    },
    '& .MuiFormControlLabel-root': {
      marginLeft: size === 'small' ? '0px' : size === 'medium' ? '-6px' : '-6px',
      marginRight: size === 'small' ? '16px' : size === 'medium' ? '24px' : '24px',
      '&:last-child': {
        marginRight: '0px',
      },
    },
  },
  label: {
    marginBottom: '4px',
    marginLeft: '4px',
    fontSize: theme.typography.pxToRem(14),
  },
  subLabel: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
}));

export default PaiRadioGroup;
